import { Component, OnInit, TemplateRef } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ApiCallingService } from "../../services/api-calling.service";
import { environment } from "../../../environments/environment";
import * as mapboxgl from "mapbox-gl";
import * as MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import { ConfigApi } from "../../config-api";
import { ToastrService } from "ngx-toastr";
import {
  SearchCountryField,
  CountryISO,
  PhoneNumberFormat,
} from "ngx-intl-tel-input";

@Component({
  selector: "app-vending-store-pickup-location",
  templateUrl: "./vending-store-pickup-location.component.html",
  styleUrls: ["./vending-store-pickup-location.component.css"],
})
export class VendingStorePickupLocationComponent implements OnInit {
  pickupLocations: any[] = [];
  pickupForm: FormGroup = new FormGroup({});
  operatorId: string = "";

  private mapboxAccessToken = environment.mapbox.accessToken;
  map: any;
  lat: number = 0;
  lng: number = 0;

  editingLocationId: string | null = null;
  modalTitle: string = "Create Pickup Location";
  saveButtonText: string = "Save";

  locationToDelete: any = null;
  separateDialCode = true;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [
    CountryISO.India,
    CountryISO.UnitedStates,
    CountryISO.UnitedKingdom,
  ];

  constructor(
    private apiService: ApiCallingService,
    private fb: FormBuilder,
    private modalService: NgbModal,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    const user = localStorage.getItem("Auth-user");
    if (user) {
      this.operatorId = JSON.parse(user)._id;
    }
    this.initForm();
    this.getPickupLocations();
  }

  initForm() {
    this.pickupForm = this.fb.group({
      name: ["", Validators.required],
      mobile: ["", [Validators.required]],
      address: ["", Validators.required],
      latitude: ["", Validators.required],
      longitude: ["", Validators.required],
      isDefault: [false],
      deliveryTiming: [""],
    });
  }

  openModal(content: any, location: any = null) {
    this.modalService.open(content, { centered: true, size: "lg" });

    if (location) {
      console.log("Location", location);
      this.editingLocationId = location._id;
      this.modalTitle = "Update Pickup Location";
      this.saveButtonText = "Update";

      this.lat = location.location.coordinates[1];
      this.lng = location.location.coordinates[0];

      let existingMobile = (location.mobile || "").toString();
      let existingDialCode = (location.diallingCode || "").toString();

      let fullMobile = existingMobile;

      if (!existingMobile.startsWith("+") && existingDialCode) {
        if (!existingDialCode.startsWith("+")) {
          existingDialCode = "+" + existingDialCode;
        }
        fullMobile = existingMobile;
      }

      this.pickupForm.patchValue({
        name: location.name,
        mobile: fullMobile,
        address: location.address,
        latitude: location.location.coordinates[1],
        longitude: location.location.coordinates[0],
        isDefault: location.isDefault,
        deliveryTiming: location.deliveryTiming || "",
      });
    } else {
      this.editingLocationId = null;
      this.modalTitle = "Create Pickup Location";
      this.saveButtonText = "Save";

      this.pickupForm.reset({
        isDefault: false,
      });
      this.lat = 0;
      this.lng = 0;
    }

    setTimeout(() => {
      this.initializeMap();
    }, 300);
  }

  openDeleteModal(location: any, content: any) {
    this.locationToDelete = location;
    this.modalService.open(content, { centered: true });
  }

  confirmDelete() {
    if (!this.locationToDelete) {
      return;
    }

    const url =
      ConfigApi.URLS.VENDING_CATEGORY.PickupLocationDelete +
      "?address_id=" +
      this.locationToDelete._id;

    this.apiService.callAPI({}, "DELETE", url).subscribe(
      (res: any) => {
        if (this.apiService.handleSuccess(res)) {
          this.toastr.success(res.msg);
          this.getPickupLocations();
          this.modalService.dismissAll();
          this.locationToDelete = null;
        } else {
          this.toastr.error(res.msg);
        }
      },
      (err) => {
        this.toastr.error(err.error.msg || "something went wrong");
      }
    );
  }

  getPickupLocations() {
    this.apiService
      .callAPI(
        {},
        "GET",
        ConfigApi.URLS.VENDING_CATEGORY.PickupLocationList +
          "?operator_id=" +
          this.operatorId
      )
      .subscribe(
        (res: any) => {
          if (res.success) {
            this.pickupLocations = res.data;
          } else {
            console.error("Failed to fetch locations", res);
          }
        },
        (error: any) => {
          console.error("Error fetching pickup locations:", error);
        }
      );
  }

  createPickupLocation() {
    if (this.pickupForm.invalid) {
      Object.keys(this.pickupForm.controls).forEach((key) => {
        const controlErrors = this.pickupForm.get(key)?.errors;
        if (controlErrors != null) {
          console.log(
            "Key control: " +
              key +
              ", keyError: " +
              JSON.stringify(controlErrors)
          );
        }
      });
      this.pickupForm.markAllAsTouched();
      return;
    }

    const mobileData = this.pickupForm.value.mobile;
    let finalMobile = "";
    let finalDialCode = "";
    let finalCountryIso = "";

    if (mobileData && typeof mobileData === "object") {
      const dialCode = mobileData.dialCode || "";
      const e164Number = mobileData.e164Number || "";
      if (e164Number && dialCode) {
        finalMobile = e164Number.replace(dialCode, "").replace(/\s/g, "");
      }

      if (!finalMobile) {
        finalMobile = mobileData.nationalNumber
          ? mobileData.nationalNumber.replace(/\s/g, "").replace(/^0/, "")
          : "";
      }

      if (mobileData.dialCode) {
        const cleanDialCode = mobileData.dialCode.toString().replace(/^\+/, "");
        finalDialCode = "+" + cleanDialCode;
      }

      finalCountryIso = mobileData.countryCode ? mobileData.countryCode : "";
    } else if (typeof mobileData === "string") {
      finalMobile = mobileData;
    }

    const payload: any = {
      operator_id: this.operatorId,
      name: this.pickupForm.value.name,
      address: this.pickupForm.value.address,
      latitude: Number(this.pickupForm.value.latitude),
      longitude: Number(this.pickupForm.value.longitude),
      isDefault: this.pickupForm.value.isDefault,
      mobile: finalMobile,
      diallingCode: finalDialCode,
      country_iso_code: finalCountryIso,
      deliveryTiming: this.pickupForm.value.deliveryTiming,
    };

    let url = ConfigApi.URLS.VENDING_CATEGORY.PickupLocationCreate;
    let method = "POST";

    if (this.editingLocationId) {
      url = `${ConfigApi.URLS.VENDING_CATEGORY.PickupLocationUpdate}/${this.editingLocationId}`;
      method = "PUT";
    }

    this.apiService.callAPI(payload, method, url).subscribe(
      (res: any) => {
        if (this.apiService.handleSuccess(res)) {
          this.toastr.success(res.msg);
          this.getPickupLocations();
          this.modalService.dismissAll();
        } else {
          this.toastr.error(res.msg);
        }
      },
      (err) => {
        this.toastr.error(err.error.msg || "something went wrong");
      }
    );
  }

  editPickupLocation(location: any, content: any) {
    this.openModal(content, location);
  }

  deletePickupLocation(id: string) {
    const url = ConfigApi.URLS.VENDING_CATEGORY.PickupLocationDelete + "/" + id;
    this.apiService.callAPI({}, "DELETE", url).subscribe(
      (res: any) => {
        if (this.apiService.handleSuccess(res)) {
          this.toastr.success(res.msg);
          this.modalService.dismissAll();
          this.getPickupLocations();
        } else {
          this.toastr.error(res.msg);
        }
      },
      (err) => {
        this.toastr.error(err.error.msg || "something went wrong");
      }
    );
  }

  initializeMap() {
    (mapboxgl as any).accessToken = this.mapboxAccessToken;
    const initialLat = this.lat || 0;
    const initialLng = this.lng || 0;

    try {
      this.map = new mapboxgl.Map({
        container: "pickupLocationMap",
        style: "mapbox://styles/mapbox/streets-v11",
        center: [initialLng, initialLat],
        zoom: 12,
      });

      const geocoder = new (MapboxGeocoder as any)({
        accessToken: (mapboxgl as any).accessToken,
        mapboxgl: mapboxgl,
        placeholder: "Search for location",
        marker: false,
      });

      this.map.addControl(geocoder);

      let marker: mapboxgl.Marker | null = null;
      if (this.lat !== 0 && this.lng !== 0) {
        marker = new mapboxgl.Marker({ draggable: true })
          .setLngLat([initialLng, initialLat])
          .addTo(this.map);
      }

      this.map.on("click", (e: any) => {
        this.lat = e.lngLat.lat;
        this.lng = e.lngLat.lng;
        this.updateFormCoordinates();

        if (!marker) {
          marker = new mapboxgl.Marker({ draggable: true })
            .setLngLat([this.lng, this.lat])
            .addTo(this.map);

          marker.on("dragend", () => {
            const lngLat = marker!.getLngLat();
            this.lat = lngLat.lat;
            this.lng = lngLat.lng;
            this.updateFormCoordinates();
            this.reverseGeocode(lngLat);
          });
        } else {
          marker.setLngLat([this.lng, this.lat]);
        }
        this.reverseGeocode(e.lngLat);
      });

      geocoder.on("result", (event: any) => {
        const coordinates = event.result.center;
        this.lng = coordinates[0];
        this.lat = coordinates[1];
        this.updateFormCoordinates();

        if (!marker) {
          marker = new mapboxgl.Marker({ draggable: true })
            .setLngLat(coordinates)
            .addTo(this.map);
          marker.on("dragend", () => {
            const lngLat = marker!.getLngLat();
            this.lat = lngLat.lat;
            this.lng = lngLat.lng;
            this.updateFormCoordinates();
            this.reverseGeocode(lngLat);
          });
        } else {
          marker.setLngLat(coordinates);
        }

        this.pickupForm.patchValue({
          address: event.result.place_name,
        });
      });
    } catch (error) {
      console.error("Error initializing map:", error);
    }
  }

  updateFormCoordinates() {
    this.pickupForm.patchValue({
      latitude: this.lat,
      longitude: this.lng,
    });
  }

  reverseGeocode(lngLat: any) {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lngLat.lng},${lngLat.lat}.json?access_token=${this.mapboxAccessToken}`;
    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        if (data.features.length > 0) {
          this.pickupForm.patchValue({
            address: data.features[0].place_name,
          });
          const geocoderInput: any = document.querySelector(
            ".mapboxgl-ctrl-geocoder--input"
          );
          if (geocoderInput) {
            geocoderInput.value = data.features[0].place_name;
          }
        }
      })
      .catch((error) => {
        console.error("Error reverse geocoding:", error);
      });
  }
}
